import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
    FaThLarge, 
    FaBuilding,
    FaUserTie,
    FaUsers,
    FaBell,
    FaUserCircle,
    FaLock,
    FaSignOutAlt,
    FaChevronDown,
    FaPlusCircle,
    FaList,
    FaTimes,
    FaCalculator
} from 'react-icons/fa';
import { useMobileMenu } from '../contexts/MobileMenuContext';
import './Sidebar.css';

const Sidebar = () => {
    const location = useLocation();
    const { isMobileMenuOpen, toggleMobileMenu } = useMobileMenu();
    const [unreadCount, setUnreadCount] = useState(0);
    const [openDropdown, setOpenDropdown] = useState(null);
    const baseUrl = "https://api.burrayhr.com";

    useEffect(() => {
        fetchNotices();
        // Poll for new notices every 30 seconds
        const interval = setInterval(fetchNotices, 30000);
        return () => clearInterval(interval);
    }, []);

    const fetchNotices = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/notices/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            // Count unread notices
            const unreadNotices = response.data.filter(notice => !notice.is_read);
            setUnreadCount(unreadNotices.length);
        } catch (error) {
            console.error('Error fetching notices:', error);
        }
    };

    const handleMenuClick = (item) => {
        // Close mobile menu when a link is clicked
        if (window.innerWidth <= 768) {
            toggleMobileMenu();
        }

        // Reset dropdown if it's a dropdown item
        if (item.type === 'dropdown') {
            setOpenDropdown(null);
        }

        // Handle specific item actions
        if (item.onClick) {
            item.onClick();
        }

        // If clicking on notices, mark notifications as read
        if (item.path === '/overall-manager/notices') {
            setUnreadCount(0);
        }
    };

    const menuItems = [
        {
            path: "/overall-manager/dashboard",
            name: "Dashboard",
            icon: <FaThLarge />
        },
        {
            type: 'dropdown',
            name: "Companies",
            icon: <FaBuilding />,
            isOpen: openDropdown === 'companies',
            toggle: () => setOpenDropdown(openDropdown === 'companies' ? null : 'companies'),
            items: [
                {
                    path: "/companies/add",
                    name: "Add Company",
                    icon: <FaPlusCircle />
                },
                {
                    path: "/companies/view",
                    name: "View Companies",
                    icon: <FaList />
                }
            ]
        },
        {
            type: 'dropdown',
            name: "Admin Managers",
            icon: <FaUserTie />,
            isOpen: openDropdown === 'managers',
            toggle: () => setOpenDropdown(openDropdown === 'managers' ? null : 'managers'),
            items: [
                {
                    path: "/admin-managers/add",
                    name: "Add Admin Manager",
                    icon: <FaPlusCircle />
                },
                {
                    path: "/admin-managers/view",
                    name: "View Admin Managers",
                    icon: <FaList />
                }
            ]
        },
        {
            path: "/formulaes",
            name: "Formulaes",
            icon: <FaCalculator />
        },
        {
            path: "/",
            name: "Logout",
            icon: <FaSignOutAlt />,
            onClick: () => {
                localStorage.clear();
            }
        }
    ];

    const renderMenuItem = (item) => {
        if (item.type === 'dropdown') {
            return (
                <div key={item.name} className="menu-dropdown">
                    <div 
                        className={`menu-item ${item.isOpen ? 'active' : ''}`} 
                        onClick={item.toggle}
                    >
                        <div className="menu-item-content">
                            <span className="icon">{item.icon}</span>
                            <span className="menu-text">{item.name}</span>
                        </div>
                        <FaChevronDown className={`dropdown-arrow ${item.isOpen ? 'open' : ''}`} />
                    </div>
                    <div className={`dropdown-content ${item.isOpen ? 'show' : ''}`}>
                        {item.items.map((subItem) => (
                            <Link 
                                to={subItem.path} 
                                key={subItem.name}
                                className={`menu-item sub-item ${location.pathname === subItem.path ? 'active' : ''}`}
                                onClick={() => handleMenuClick(subItem)}
                            >
                                <div className="menu-item-content">
                                    <span className="icon">{subItem.icon}</span>
                                    <span className="menu-text">{subItem.name}</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            );
        }

        return (
            <Link 
                to={item.path} 
                key={item.name} 
                className={`menu-item ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => handleMenuClick(item)}
            >
                <div className="menu-item-content">
                    <span className="icon">{item.icon}</span>
                    <span className="menu-text">{item.name}</span>
                </div>
                {item.notification > 0 && (
                    <span className="notification-badge">{item.notification}</span>
                )}
            </Link>
        );
    };

    return (
        <>
            <div className={`sidebar ${isMobileMenuOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    <img 
                        src="https://burrayhr.com/burray.png" 
                        alt="Burray Logo" 
                        className="sidebar-logo" 
                    />
                    <h3>Administrator</h3>
                    <button 
                        className="mobile-close-btn" 
                        onClick={toggleMobileMenu}
                    >
                        <FaTimes />
                    </button>
                </div>
                <div className="sidebar-menu">
                    {menuItems.map(item => renderMenuItem(item))}
                </div>
            </div>
            <div 
                className={`mobile-overlay ${isMobileMenuOpen ? 'show' : ''}`} 
                onClick={toggleMobileMenu}
            ></div>
        </>
    );
};

export default Sidebar;
